import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const NAV_ITEMS = [
  { path: '/admin/definitions', icon: 'ri-settings-3-line', label: '定义管理' },
  { path: '/admin/products', icon: 'ri-box-3-line', label: '商品管理' },
  { path: '/admin/messages', icon: 'ri-message-2-line', label: '留言管理' },
];

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function AdminLayout({ children, title }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-60 bg-white border-r border-[#ede9e3] z-30 flex flex-col transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:z-auto`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-[#ede9e3]">
          <div className="flex items-center justify-center bg-[#C8714A] rounded-lg flex-shrink-0 px-2 py-1.5">
            <img
              src="https://storage.readdy-site.link/project_files/d60e0add-d934-490f-b5d8-9398c2931d82/d3cef690-9814-4ee5-8d70-6302dcf14eac_2.png?v=8f456bca08344c0bb883917c789b2e55"
              alt="Koorpet"
              className="h-5 w-auto object-contain"
            />
          </div>
          <div>
            <div className="text-xs text-[#aaa]">后台管理</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 flex flex-col gap-1">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer
                  ${active
                    ? 'bg-[#C8714A] text-white'
                    : 'text-[#555] hover:bg-[#f7f5f2] hover:text-[#1a1a1a]'
                  }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-base`}></i>
                </div>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div className="px-3 py-4 border-t border-[#ede9e3]">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center bg-[#f0ebe4] rounded-full flex-shrink-0">
              <i className="ri-user-line text-[#C8714A] text-sm"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-[#333] truncate">{user?.email}</div>
              <div className="text-xs text-[#aaa]">管理员</div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#888] hover:bg-red-50 hover:text-red-500 transition-all cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line text-base"></i>
            </div>
            {signingOut ? '退出中...' : '退出登录'}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#888] hover:bg-[#f7f5f2] hover:text-[#333] transition-all cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-external-link-line text-base"></i>
            </div>
            查看前台网站
          </a>
          <Link
            to="/admin/scheduler"
            onClick={() => setSidebarOpen(false)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer
              ${location.pathname === '/admin/scheduler'
                ? 'bg-[#C8714A] text-white'
                : 'text-[#888] hover:bg-[#f7f5f2] hover:text-[#333]'
              }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-timer-line text-base"></i>
            </div>
            定时任务
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-[#ede9e3] px-6 py-4 flex items-center gap-4 sticky top-0 z-10">
          <button
            className="lg:hidden w-8 h-8 flex items-center justify-center text-[#555] hover:text-[#1a1a1a] cursor-pointer"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <h1 className="text-base font-semibold text-[#1a1a1a]">{title}</h1>
        </header>

        {/* Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
